import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, BadgeDollarSign, Upload, Send, Loader, Lightbulb, Plus, TrendingUp, TrendingDown, ArrowUpRight, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PricingOptimizer = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.pricing;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);
  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [dataText, setDataText] = useState('');
  const [acctAvailable, setAcctAvailable] = useState(false);
  const [acctLoading, setAcctLoading] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Pricing Optimizer', lastAssistant);

  const uploadData = async (file) => {
    if (!file) return;
    setLoading(true); setView('result');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/compass/pricing/upload-data?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSessionId(res.data.session_id); setPricingData(res.data.pricing_data);
      setMessages([{ role: 'user', content: `Uploaded ${file.name}` }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Upload failed.' : 'Upload mislukt.' }]); }
    finally { setLoading(false); }
  };

  // Zijn er geimporteerde cijfers? Zo niet, dan tonen we de knop niet.
  useEffect(() => {
    let levend = true;
    axios.get(`${API}/compass/pricing/accounting-check`, { headers })
      .then(res => { if (levend) setAcctAvailable(!!res.data?.has_data); })
      .catch(() => {});
    return () => { levend = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Analyseren op wat er al geimporteerd is, zonder dat de gebruiker iets hoeft te plakken.
  const analyzeFromAccounting = async () => {
    setAcctLoading(true); setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/pricing/analyze-text`,
        { data_text: '', use_accounting: true, language: lang }, { headers });
      setSessionId(res.data.session_id); setPricingData(res.data.pricing_data);
      setMessages([
        { role: 'user', content: lang === 'nl' ? 'Analyseer mijn geimporteerde boekhoudcijfers.' : 'Analyse my imported accounting figures.' },
        { role: 'assistant', content: res.data.analysis },
      ]);
    } catch (e) {
      setMessages([{ role: 'assistant', content: e.response?.data?.detail || (lang === 'en' ? 'Analysis failed.' : 'Analyse mislukt.') }]);
    } finally { setAcctLoading(false); setLoading(false); }
  };

  const analyzeText = async () => {
    if (!((dataText) == null ? '' : String(dataText)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/pricing/analyze-text`, { data_text: dataText, language: lang }, { headers });
      setSessionId(res.data.session_id); setPricingData(res.data.pricing_data);
      setMessages([{ role: 'user', content: dataText }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/pricing/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setDataText(''); setPricingData(null); };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> AI Tools</button>
          <h1 className="heading-1"><BadgeDollarSign size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="pricing-start">
            <div className="auto-input-card">
              <div className="auto-input-header"><BadgeDollarSign size={20} /><h3 className="heading-3">{tp.title}</h3></div>
              <p className="body-small text-secondary">{lang === 'nl' ? 'Upload product-/prijsdata of beschrijf je producten voor margeanalyse en optimalisatie.' : 'Upload product/pricing data or describe your products for margin analysis and optimization.'}</p>
              <div className="explorer-dropzone" style={{ margin: '1rem 0', padding: '1.5rem' }} onClick={() => fileRef.current?.click()} data-testid="pricing-dropzone">
                <Upload size={24} /><span className="body-small">{tp.uploadData}</span>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => uploadData(e.target.files[0])} />
              </div>
              <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{tp.orDescribe}</div>
              {acctAvailable && (
                <button
                  className="btn-primary"
                  style={{ width: '100%', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                  onClick={analyzeFromAccounting}
                  disabled={acctLoading}
                  data-testid="pricing-use-accounting-btn"
                >
                  {acctLoading ? <Loader className="spin" size={16} /> : <Link2 size={16} />}
                  {lang === 'nl' ? 'Gebruik mijn boekhoudcijfers' : 'Use my accounting figures'}
                </button>
              )}
              <textarea className="form-input auto-textarea" placeholder={tp.analyzePlaceholder} value={dataText} onChange={(e) => setDataText(e.target.value)} rows={5} data-testid="pricing-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={analyzeText} disabled={!((dataText) == null ? '' : String(dataText)).trim()} data-testid="pricing-analyze-btn">
                <BadgeDollarSign size={16} /> {tp.analyzeBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="pricing-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-green-200)' }}><BadgeDollarSign size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        {pricingData && (
          <div className="sim-sidebar-stats" data-testid="pricing-stats">
            {pricingData.revenue_opportunity && <div className="sim-stat-item sim-rec-go"><ArrowUpRight size={14} /><span className="body-small">{lang === 'nl' ? 'Kans' : 'Opportunity'}</span><span className="body-small" style={{ fontWeight: 600 }}>{pricingData.revenue_opportunity}</span></div>}
            {pricingData.recommendations && <div className="sim-stat-item"><TrendingUp size={14} /><span className="body-small">{lang === 'nl' ? 'Suggesties' : 'Suggestions'}</span><span className="body-small" style={{ fontWeight: 600 }}>{pricingData.recommendations.length}</span></div>}
            {pricingData.underpriced && <div className="sim-stat-item" style={{ background: 'var(--accent-orange-200)' }}><TrendingDown size={14} /><span className="body-small">{lang === 'nl' ? 'Ondergeprijsd' : 'Underpriced'}</span><span className="body-small" style={{ fontWeight: 600 }}>{pricingData.underpriced.length}</span></div>}
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {t.common.newAnalysis}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          {pricingData?.recommendations && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem', padding: '1.25rem 0 0' }} data-testid="pricing-cards">
              {pricingData.recommendations.slice(0, 4).map((r, i) => (
                <div key={i} className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-green-200)' }}>
                  <span className="body-small" style={{ fontWeight: 600 }}>{r.product}</span>
                  <span className="caption">{r.current_price} → {r.suggested_price}</span>
                  <span className="caption text-secondary">{r.expected_impact}</span>
                  <span className={`compass-difficulty compass-diff-${r.confidence?.toLowerCase() === 'high' ? 'beginner' : 'intermediate'}`} style={{ alignSelf: 'flex-start' }}>{r.confidence}</span>
                </div>
              ))}
            </div>
          )}
          <div className="compass-chat-messages" data-testid="pricing-messages">
            {loading && messages.length === 0 && <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Prijzen analyseren...' : 'Analyzing pricing...'}</h3></div>}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Berekenen...' : 'Calculating...'}</div></div>}
            <div ref={chatEndRef} />
          </div>
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="pricing-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PricingOptimizer;
